import { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getTaskForm } from '@epics/taskFormManagement/api/getTaskFormApiService';
import { getCertificateTemplate } from '../api/getCertificateTemplateApiService';
import { updateCertificateTemplate } from '../api/updateCertificateTemplateApiService';
import { CERTIFICATE_LABELS } from '../constants/labels';
import type {
  CertificateFormField,
  CertificateTemplateDetail,
  CreateCertificateTemplateFormState,
} from '../types';

export interface EditFormErrors {
  certTemplateName?: string;
  headerField?: string;
  footerField?: string;
  subtitle?: string;
  selectedFormFieldIds?: string;
  general?: string;
}

function validateForPreview(
  state: CreateCertificateTemplateFormState
): EditFormErrors {
  const errors: EditFormErrors = {};

  if (!state.certTemplateName.trim()) {
    errors.certTemplateName = CERTIFICATE_LABELS.ERROR_TEMPLATE_NAME_REQUIRED;
  } else if (state.certTemplateName.length > 200) {
    errors.certTemplateName = CERTIFICATE_LABELS.ERROR_TEMPLATE_NAME_MAX;
  }

  if (!state.headerField.trim()) {
    errors.headerField = CERTIFICATE_LABELS.ERROR_HEADER_REQUIRED;
  } else if (state.headerField.length > 200) {
    errors.headerField = CERTIFICATE_LABELS.ERROR_HEADER_MAX;
  }

  if (!state.footerField.trim()) {
    errors.footerField = CERTIFICATE_LABELS.ERROR_FOOTER_REQUIRED;
  } else if (state.footerField.length > 200) {
    errors.footerField = CERTIFICATE_LABELS.ERROR_FOOTER_MAX;
  }

  if (state.subtitle && state.subtitle.length > 500) {
    errors.subtitle = CERTIFICATE_LABELS.ERROR_SUBTITLE_MAX;
  }

  if (state.selectedFormFieldIds.length === 0) {
    errors.selectedFormFieldIds = CERTIFICATE_LABELS.ERROR_FIELDS_REQUIRED;
  }

  return errors;
}

function hasErrors(errors: EditFormErrors): boolean {
  return Object.values(errors).some(Boolean);
}

const EMPTY_STATE: CreateCertificateTemplateFormState = {
  certTemplateName: '',
  formTemplateId: '',
  formTemplateName: '',
  formTemplateDisplayId: '',
  headerField: '',
  footerField: '',
  subtitle: '',
  includeTechnicianDetail: false,
  selectedFormFieldIds: [],
  availableFormFields: [],
};

/**
 * Manages the edit-certificate-template flow.
 *
 * Differences from `useCreateCertTemplate`:
 * - Loads existing template by ID and seeds form state from the response.
 * - Loads the linked task form's fields and preserves the original selection.
 * - Task form cannot be changed — only the task form ID is read-only context.
 * - Submits PATCH instead of POST; navigates back to the view page on success.
 */
export function useEditCertTemplate(templateId: string) {
  const navigate = useNavigate();
  const location = useLocation();

  // Where to return after save/cancel — the page the user entered edit from
  // (listing or view). Falls back to the view page for direct URL access.
  const returnTo =
    (location.state as { returnTo?: string } | null)?.returnTo ??
    `/Certyfikaty/Podgląd-szablonu/${templateId}`;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [template, setTemplate] = useState<CertificateTemplateDetail | null>(
    null
  );
  const [formState, setFormState] =
    useState<CreateCertificateTemplateFormState>(EMPTY_STATE);
  const [initialState, setInitialState] =
    useState<CreateCertificateTemplateFormState>(EMPTY_STATE);

  const [loadingFields, setLoadingFields] = useState(false);
  const [errors, setErrors] = useState<EditFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isErrorSnackbarOpen, setIsErrorSnackbarOpen] = useState(false);
  const [snackbarError, setSnackbarError] = useState('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [validationAttemptCount, setValidationAttemptCount] = useState(0);

  // ─── Load template ──────────────────────────────────────────────────────────

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setLoadError('');
    setNotFound(false);

    async function load() {
      const result = await getCertificateTemplate(templateId);
      if (!mounted) {
        return;
      }
      if (result.notFound) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      if (!result.success || !result.data) {
        setLoadError(result.error ?? CERTIFICATE_LABELS.VIEW_ERROR_GENERIC);
        setLoading(false);
        return;
      }
      const t = result.data;
      setTemplate(t);

      // Seed the form state from the API response. availableFormFields will
      // be filled in once the task form is loaded below.
      const seedSelectedIds = t.selectedFormFields.map((f) => f.id);
      const seedAvailableFields: CertificateFormField[] =
        t.selectedFormFields.map((f) => ({ id: f.id, label: f.label }));
      const seed: CreateCertificateTemplateFormState = {
        certTemplateName: t.certTemplateName,
        formTemplateId: t.formTemplate?.id ?? '',
        formTemplateName: t.formTemplate?.name ?? '',
        formTemplateDisplayId: t.formTemplate?.formTemplateId ?? '',
        headerField: t.headerField,
        footerField: t.footerField,
        subtitle: t.subtitle ?? '',
        includeTechnicianDetail: t.includeTechnicianDetail,
        selectedFormFieldIds: seedSelectedIds,
        availableFormFields: seedAvailableFields,
      };
      setFormState(seed);
      setInitialState(seed);

      // Try to load full task-form fields so the user can toggle additional
      // fields. If the form can't be fetched (deleted, etc.), we fall back to
      // showing only the originally-selected fields.
      if (t.formTemplate?.id) {
        setLoadingFields(true);
        const formResult = await getTaskForm(t.formTemplate.id);
        if (!mounted) {
          return;
        }
        if (formResult.success && formResult.data) {
          const available = formResult.data.schema.sections.flatMap((s) =>
            s.fields
              .filter((f) => f.type !== 'signature')
              .map((f) => ({ id: f.id, label: f.config.label }))
          );
          // Merge any originally-selected fields not present in the latest
          // schema so they remain visible & toggleable.
          const merged: CertificateFormField[] = [...available];
          for (const sel of seedAvailableFields) {
            if (!merged.some((m) => m.id === sel.id)) {
              merged.push(sel);
            }
          }
          setFormState((prev) => ({ ...prev, availableFormFields: merged }));
          setInitialState((prev) => ({
            ...prev,
            availableFormFields: merged,
          }));
        }
        setLoadingFields(false);
      }
      setLoading(false);
    }

    void load();
    return () => {
      mounted = false;
    };
  }, [templateId]);

  // ─── Dirty check ────────────────────────────────────────────────────────────

  const isDirty =
    formState.certTemplateName !== initialState.certTemplateName ||
    formState.headerField !== initialState.headerField ||
    formState.footerField !== initialState.footerField ||
    formState.subtitle !== initialState.subtitle ||
    formState.includeTechnicianDetail !==
      initialState.includeTechnicianDetail ||
    formState.selectedFormFieldIds.length !==
      initialState.selectedFormFieldIds.length ||
    formState.selectedFormFieldIds.some(
      (id) => !initialState.selectedFormFieldIds.includes(id)
    );

  // ─── Field setters ──────────────────────────────────────────────────────────

  const setCertTemplateName = useCallback(
    (value: string) => {
      setFormState((prev) => ({ ...prev, certTemplateName: value }));
      if (errors.certTemplateName) {
        setErrors((prev) => {
          const { certTemplateName: _, ...rest } = prev;
          return rest;
        });
      }
    },
    [errors.certTemplateName]
  );

  const setHeaderField = useCallback(
    (value: string) => {
      setFormState((prev) => ({ ...prev, headerField: value }));
      if (errors.headerField) {
        setErrors((prev) => {
          const { headerField: _, ...rest } = prev;
          return rest;
        });
      }
    },
    [errors.headerField]
  );

  const setFooterField = useCallback(
    (value: string) => {
      setFormState((prev) => ({ ...prev, footerField: value }));
      if (errors.footerField) {
        setErrors((prev) => {
          const { footerField: _, ...rest } = prev;
          return rest;
        });
      }
    },
    [errors.footerField]
  );

  const setSubtitle = useCallback(
    (value: string) => {
      setFormState((prev) => ({ ...prev, subtitle: value }));
      if (errors.subtitle) {
        setErrors((prev) => {
          const { subtitle: _, ...rest } = prev;
          return rest;
        });
      }
    },
    [errors.subtitle]
  );

  const setIncludeTechnicianDetail = useCallback((value: boolean) => {
    setFormState((prev) => ({ ...prev, includeTechnicianDetail: value }));
  }, []);

  const handleToggleField = useCallback(
    (fieldId: string) => {
      setFormState((prev) => {
        const already = prev.selectedFormFieldIds.includes(fieldId);
        return {
          ...prev,
          selectedFormFieldIds: already
            ? prev.selectedFormFieldIds.filter((id) => id !== fieldId)
            : [...prev.selectedFormFieldIds, fieldId],
        };
      });
      if (errors.selectedFormFieldIds) {
        setErrors((prev) => {
          const { selectedFormFieldIds: _, ...rest } = prev;
          return rest;
        });
      }
    },
    [errors.selectedFormFieldIds]
  );

  // ─── Preview ────────────────────────────────────────────────────────────────

  const handleOpenPreview = useCallback(() => {
    const validationErrors = validateForPreview(formState);
    if (hasErrors(validationErrors)) {
      setErrors(validationErrors);
      setValidationAttemptCount((n) => n + 1);
      return;
    }
    setErrors({});
    setIsPreviewOpen(true);
  }, [formState]);

  const handleClosePreview = useCallback(() => {
    setIsPreviewOpen(false);
  }, []);

  // ─── Submit (from preview) ──────────────────────────────────────────────────

  const handleSaveFromPreview = useCallback(async () => {
    setIsSubmitting(true);
    const payload = {
      cert_template_name: formState.certTemplateName.trim(),
      header_field: formState.headerField.trim(),
      footer_field: formState.footerField.trim(),
      include_technician_detail: formState.includeTechnicianDetail,
      selected_form_field_ids: formState.selectedFormFieldIds,
      ...(formState.subtitle.trim()
        ? { subtitle: formState.subtitle.trim() }
        : {}),
    };

    const result = await updateCertificateTemplate(templateId, payload);
    setIsSubmitting(false);

    if (result.success) {
      navigate(returnTo, {
        state: { navigationSuccessMessage: CERTIFICATE_LABELS.EDIT_SUCCESS },
      });
    } else {
      setSnackbarError(result.error ?? CERTIFICATE_LABELS.EDIT_SUCCESS);
      setIsErrorSnackbarOpen(true);
    }
  }, [formState, templateId, navigate, returnTo]);

  // ─── Cancel ─────────────────────────────────────────────────────────────────

  const handleCancel = useCallback(() => {
    if (isDirty) {
      setIsCancelModalOpen(true);
    } else {
      navigate(returnTo);
    }
  }, [isDirty, navigate, returnTo]);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    setIsCancelModalOpen(false);
    navigate(returnTo);
  }, [navigate, returnTo]);

  const handleCloseErrorSnackbar = useCallback(() => {
    setIsErrorSnackbarOpen(false);
  }, []);

  return {
    loading,
    loadError,
    notFound,
    template,
    formState,
    loadingFields,
    errors,
    isSubmitting,
    isCancelModalOpen,
    isErrorSnackbarOpen,
    snackbarError,
    isPreviewOpen,
    isDirty,
    validationAttemptCount,
    setCertTemplateName,
    setHeaderField,
    setFooterField,
    setSubtitle,
    setIncludeTechnicianDetail,
    handleToggleField,
    handleOpenPreview,
    handleClosePreview,
    handleSaveFromPreview,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseErrorSnackbar,
  };
}
